import { useTranslation } from 'react-i18next';
import { MergedScheduledPayment, NameOfFrequency, NameOfFrequencyOriginal, capitalizeFirstLetterOfWords } from 'vopay';

export function useI18nLabels() {
  const { t } = useTranslation('translations');

  const mapFrequency: Record<NameOfFrequency | NameOfFrequencyOriginal, string> = {
    monthly: t('Shared.FrequencyOptions.monthly', { defaultValue: 'Monthly' }),
    weekly: t('Shared.FrequencyOptions.weekly', { defaultValue: 'Weekly' }),
    biweekly: t('Shared.FrequencyOptions.biweekly', { defaultValue: 'Bi-Weekly' }),
    '3 months': t('Shared.FrequencyOptions.quarterly', { defaultValue: 'Quarterly' }),
    '6 months': t('Shared.FrequencyOptions.semiAnnually', { defaultValue: 'Semi-Annually' }),
    yearly: t('Shared.FrequencyOptions.annually', { defaultValue: 'Annually' }),
    bimonthly: t('Shared.FrequencyOptions.bimonthly', { defaultValue: 'Bi-Monthly' }),
    'semi-monthly': t('Shared.FrequencyOptions.semiMonthly', { defaultValue: 'Semi-Monthly' }),
    daily: t('Shared.FrequencyOptions.daily', { defaultValue: 'Daily' }),
    single: t('Shared.FrequencyOptions.single', { defaultValue: 'Single' }),
    'bi-weekly': t('Shared.FrequencyOptions.biweekly', { defaultValue: 'Bi-Weekly' }),
    quarterly: t('Shared.FrequencyOptions.quarterly', { defaultValue: 'Quarterly' }),
    'semi-annually': t('Shared.FrequencyOptions.semiAnnually', { defaultValue: 'Semi-Annually' }),
    annually: t('Shared.FrequencyOptions.annually', { defaultValue: 'Annually' }),
    weekdays: t('Shared.FrequencyOptions.weekdays', { defaultValue: 'Weekdays' }),
    custom: t('Shared.FrequencyOptions.custom', { defaultValue: 'Custom' }),
  };

  // The maps already hold translated strings — return the lookup directly. Passing
  // it back through t() would treat the rendered label as a key.
  const frequencyLabelMapper = (value?: NameOfFrequency | NameOfFrequencyOriginal | ''): string => {
    if (!value) {
      return '';
    }

    return mapFrequency[value] || '';
  };

  const mapTransferType: Record<string, string> = {
    'fund-transfer': t('Shared.TransferTypeOptions.fundTransfer', { defaultValue: 'Fund and Transfer' }),
    'transfer-withdraw': t('Shared.TransferTypeOptions.transferWithdraw', { defaultValue: 'Transfer and Withdraw' }),
    'fund-transfer-withdraw': t('Shared.TransferTypeOptions.fundTransferWithdraw', {
      defaultValue: 'Fund, Transfer and Withdraw',
    }),
  };

  const transferTypeLabelMapper = (value: string, defaultLabel?: string): string => {
    return (
      mapTransferType[value] ??
      defaultLabel ??
      t('Shared.TransferTypeOptions.fundTransferWithdraw', { defaultValue: 'Fund, Transfer and Withdraw' })
    );
  };

  const mapSemiMonthly: Record<string, string> = {
    SemiMonthFirstAndFifteenth: t('Shared.SemiMonthlyDateOptions.firstAndFifteenth', {
      defaultValue: '1st and 15th Day of the Month',
    }),
    SemiMonthFifteenthAndLast: t('Shared.SemiMonthlyDateOptions.fifteenthAndLast', {
      defaultValue: '15th and Last Day of the Month',
    }),
  };

  const semiMonthlyDateLabelMapper = (value: string): string => {
    return mapSemiMonthly[value] || '';
  };

  const mapScheduledPaymentStatus: Record<MergedScheduledPayment['Status'], string> = {
    'in progress': t('Shared.Statuses.inProgress', { defaultValue: 'In Progress' }),
    cancelled: t('Shared.Statuses.cancelled', { defaultValue: 'Cancelled' }),
    completed: t('Shared.Statuses.completed', { defaultValue: 'Completed' }),
    'pending approval': t('Shared.Statuses.pendingApproval', { defaultValue: 'Pending Approval' }),
  };

  const scheduledPaymentStatusLabelMapper = (status: MergedScheduledPayment['Status']): string => {
    return mapScheduledPaymentStatus[status] || capitalizeFirstLetterOfWords(status);
  };

  const paymentTypeLabelMapper = (paymentType: MergedScheduledPayment['PaymentType']): string => {
    return paymentType === 'debit'
      ? t('Shared.ScheduleTransactionTypes.fund', { defaultValue: 'Fund' })
      : t('Shared.ScheduleTransactionTypes.withdraw', { defaultValue: 'Withdraw' });
  };

  return {
    frequencyLabelMapper,
    transferTypeLabelMapper,
    semiMonthlyDateLabelMapper,
    scheduledPaymentStatusLabelMapper,
    paymentTypeLabelMapper,
  };
}
